import { DEFAULT_COLUMN_WIDTH } from './table.constants'
import { TableColumn } from './table.types'

// Note: in tenths of a rem to keep the math in integers - every header action is a 24x24
// HeaderActionButton with a 4px gap between them (ColumnActionsWrapper).
const HEADER_ACTION_SIZE = 24
const HEADER_ACTION_GAP = 4

// Note: the most header actions a column can ever show at once - the unpin button (pinnable),
// the sort arrow (sortable) and "..." (either) - not the ones shown right now, so a column keeps
// one width whether it's pinned, sorted or hovered.
export const getHeaderActionsCount = (
  column: Pick<TableColumn, 'title' | 'sortKey'>,
  enablePinning: boolean,
): number => {
  const canPin = enablePinning && !!column.title
  const isSortable = !!column.sortKey
  return Number(canPin) + Number(isSortable) + Number(canPin || isSortable)
}

export const getHeaderActionsWidth = (
  column: Pick<TableColumn, 'title' | 'sortKey'>,
  enablePinning: boolean,
): string | null => {
  const count = getHeaderActionsCount(column, enablePinning)
  if (!count) return null
  return `${(count * HEADER_ACTION_SIZE + (count - 1) * HEADER_ACTION_GAP) / 10}rem`
}

const toPx = (value: string) => Number.parseFloat(value) || 0

// Note: the narrowest a header cell can be with its title shown in full and clear of every
// header action - the title's (and its info icon's) natural width, the actions' reserve
// (HeaderCellContent's padding-right) and the th's own padding/border. Read from the DOM, so it
// follows the real font, the translated title and the mobile padding.
export const measureHeaderMinWidth = (headerCell: HTMLElement): number => {
  const content = headerCell.querySelector<HTMLElement>('[data-header-content]')
  if (!content) return 0

  const contentStyle = getComputedStyle(content)
  const children = Array.from(content.children) as HTMLElement[]
  // Note: scrollWidth, not the rendered width - a title that's already truncated still reports
  // its full text width. It's rounded to a whole pixel, though, so a 59.04px title would get a
  // 59px box and an ellipsis - the extra 1px per child covers that rounding.
  const childrenWidth = children.reduce((sum, child) => sum + child.scrollWidth + 1, 0)
  const gaps = Math.max(children.length - 1, 0) * toPx(contentStyle.columnGap)

  const cellStyle = getComputedStyle(headerCell)
  const cellChrome =
    toPx(cellStyle.paddingLeft) +
    toPx(cellStyle.paddingRight) +
    toPx(cellStyle.borderLeftWidth) +
    toPx(cellStyle.borderRightWidth)

  return Math.ceil(childrenWidth + gaps + toPx(contentStyle.paddingRight) + cellChrome)
}

// Note: TableColumn.width is the business width (title + the expected cell content). A column
// only grows past it when its header doesn't fit - its title plus the most header actions it can
// show (see measureHeaderMinWidth) - so a wide column with a short title stays as the page set
// it. Until the header is measured (first render) the business width is used as-is. Percentage
// widths are left as they are - they already split the table between themselves, and growing one
// would overflow it - their titles truncate before the actions instead.
export const getColumnWidth = (
  column: Pick<TableColumn, 'title' | 'sortKey' | 'width'>,
  enablePinning: boolean,
  headerMinWidth?: number,
): string => {
  const width = column.width ?? DEFAULT_COLUMN_WIDTH
  if (!headerMinWidth || !getHeaderActionsCount(column, enablePinning) || width.endsWith('%')) {
    return width
  }
  return `max(${width}, ${headerMinWidth}px)`
}
